// =============================================================================
// AGC: DROID FARM - AI Detection Loading Animations
// AGC: Loading states for AI One-Click Measuring feature
// =============================================================================

import React, { useEffect, useState } from 'react';
import { SparklesIcon } from '../icons/SparklesIcon';
import { useTranslation } from '../../hooks/useTranslation';

type DetectionPhase = 'capturing' | 'uploading' | 'analyzing' | 'detecting' | 'calculating';

interface AIDetectionLoadingProps {
  phase?: DetectionPhase;
  onCancel?: () => void;
}

/**
 * AGC: Full screen loading overlay for AI detection
 */
export const AIDetectionLoading: React.FC<AIDetectionLoadingProps> = ({
  phase = 'analyzing',
  onCancel,
}) => {
  const { t } = useTranslation();
  const [dots, setDots] = useState('');

  // AGC: Phase configuration
  const phaseConfig: Record<DetectionPhase, { label: string; icon: string }> = {
    capturing: { label: t('lotDetection.loading.capturing'), icon: '📷' },
    uploading: { label: t('lotDetection.loading.uploading'), icon: '☁️' },
    analyzing: { label: t('lotDetection.loading.analyzing'), icon: '🔍' },
    detecting: { label: t('lotDetection.loading.detecting'), icon: '📐' },
    calculating: { label: t('lotDetection.loading.calculating'), icon: '🧮' },
  };

  const config = phaseConfig[phase];

  // AGC: Animated dots effect
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(d => d.length >= 3 ? '' : d + '.');
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[90]">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 max-w-sm w-full mx-4 relative overflow-hidden">
        {/* AGC: Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-green-900/20 dark:via-gray-800 dark:to-emerald-900/20 opacity-50" />
        
        <div className="relative">
          {/* AGC: Animated icon container */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              {/* AGC: Pulsing background ring */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-green-400/20 animate-ping" />
              </div>
              {/* AGC: Main icon */}
              <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 flex items-center justify-center shadow-lg">
                <SparklesIcon size="xl" animated className="text-white" />
              </div>
            </div>
          </div>

          {/* AGC: Phase info */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-2xl">{config.icon}</span>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {config.label}{dots}
              </h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('lotDetection.loading.pleaseWait')}
            </p>
          </div>

          {/* AGC: Scanning visualization */}
          <div className="relative h-32 bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden mb-4">
            {/* AGC: Grid pattern */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute inset-2 border-2 border-dashed border-gray-300 dark:border-gray-500 rounded-lg" />
            </div>
            {/* AGC: Scanning line */}
            <div 
              className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent shadow-lg animate-scan-line"
            />
          </div>

          {/* AGC: Cancel button */}
          {onCancel && (
            <button
              onClick={onCancel}
              className="w-full py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t('common.cancel')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// AGC: Compact Loading Indicator
// =============================================================================

interface AIDetectionLoadingCompactProps {
  phase?: DetectionPhase;
}

/**
 * AGC: Compact inline loading indicator
 */
export const AIDetectionLoadingCompact: React.FC<AIDetectionLoadingCompactProps> = ({
  phase = 'analyzing',
}) => {
  const { t } = useTranslation();
  
  const phaseLabels: Record<DetectionPhase, string> = {
    capturing: t('lotDetection.loading.capturing'),
    uploading: t('lotDetection.loading.uploading'),
    analyzing: t('lotDetection.loading.analyzing'),
    detecting: t('lotDetection.loading.detecting'),
    calculating: t('lotDetection.loading.calculating'),
  };

  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 rounded-full">
      <SparklesIcon size="sm" animated className="text-green-600" />
      <span className="text-sm font-medium text-green-700 dark:text-green-400">
        {phaseLabels[phase]}
      </span>
    </div>
  );
};

// =============================================================================
// AGC: Map Overlay Loading
// =============================================================================

interface AIDetectionMapOverlayProps {
  onCancel?: () => void;
}

/**
 * AGC: Transparent overlay for map with scanning effect
 */
export const AIDetectionMapOverlay: React.FC<AIDetectionMapOverlayProps> = ({
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <div className="absolute inset-0 z-40 pointer-events-none">
      {/* AGC: Semi-transparent overlay */}
      <div className="absolute inset-0 bg-black/20" />
      
      {/* AGC: Scanning line animation */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-green-400 to-transparent animate-scan-line"
        />
      </div>
      
      {/* AGC: Corner brackets */}
      <div className="absolute inset-8">
        {/* Top-left */}
        <div className="absolute top-0 left-0 w-8 h-8 border-l-4 border-t-4 border-green-500 rounded-tl-lg" />
        {/* Top-right */}
        <div className="absolute top-0 right-0 w-8 h-8 border-r-4 border-t-4 border-green-500 rounded-tr-lg" />
        {/* Bottom-left */}
        <div className="absolute bottom-0 left-0 w-8 h-8 border-l-4 border-b-4 border-green-500 rounded-bl-lg" />
        {/* Bottom-right */}
        <div className="absolute bottom-0 right-0 w-8 h-8 border-r-4 border-b-4 border-green-500 rounded-br-lg" />
      </div>
      
      {/* AGC: Center indicator */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 flex items-center gap-3">
          <SparklesIcon size="lg" animated className="text-green-600" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {t('lotDetection.loading.analyzing')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('lotDetection.loading.detectingBoundaries')}
            </p>
          </div>
          {onCancel && (
            <button 
              onClick={onCancel}
              className="ml-2 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// AGC: Success Animation
// =============================================================================

interface AIDetectionSuccessProps {
  areaHectares: number;
  onContinue: () => void;
}

/**
 * AGC: Success state with confetti-like effect
 */
export const AIDetectionSuccess: React.FC<AIDetectionSuccessProps> = ({
  areaHectares,
  onContinue,
}) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[90]">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
        {/* AGC: Success icon */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
          <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('lotDetection.success.title')}
        </h3>
        
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          {t('lotDetection.success.areaDetected', { area: areaHectares.toFixed(2) })}
        </p>
        
        <button
          onClick={onContinue}
          className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
        >
          {t('lotDetection.success.continue')}
        </button>
      </div>
    </div>
  );
};

export default AIDetectionLoading;

